const ORG_SITE = 'https://www.dexon-technology.com'
const ORG_ID = `${ORG_SITE}/#organization`
const SITE_ID = `${ORG_SITE}/#website`

const LOGO_URL = `${ORG_SITE}/pwa-512x512.png`

export function organizationGraph() {
  const org: Record<string, any> = {
    '@type': 'Corporation',
    '@id': ORG_ID,
    'name': 'Dexon Technology',
    'legalName': 'Dexon Technology Public Company Limited',
    'alternateName': [
      'Dexon',
      'Dexon Technology PLC',
      'DEXON',
      'Dacon Inspection Technologies',
      'Dacon Inspection Company Services',
    ],
    'url': `${ORG_SITE}/`,
    'description':
      'Dexon Technology is a multi-discipline inspection and asset integrity contractor providing conventional and advanced non-destructive testing, asset integrity management and pipeline in-line inspection to the oil and gas, petrochemical, refinery, mining, power, marine and manufacturing industries. Dexon designs and manufactures its own in-line inspection tools and sensors in-house.',
    'foundingDate': '1981',
    'numberOfEmployees': { '@type': 'QuantitativeValue', 'minValue': 400 },
    'tickerSymbol': 'DEXON',
    'email': 'info@dexon-technology.com',
    'telephone': '+66-95-474-2606',

    'address': {
      '@type': 'PostalAddress',
      'streetAddress': '78/4-5 Moo 6, Sukhumvit Road',
      'addressLocality': 'Ban Chang',
      'addressRegion': 'Rayong',
      'postalCode': '21130',
      'addressCountry': 'TH',
    },

    'location': [
      orgPlace('Dexon Technology Public Company Limited — Thailand (Headquarters)', '+6633012484', {
        streetAddress: '78/4-5 Moo 6, Sukhumvit Road',
        addressLocality: 'Ban Chang',
        addressRegion: 'Rayong',
        postalCode: '21130',
        addressCountry: 'TH',
      }),
      orgPlace('DEXON Technology Pte., Ltd. — Singapore', '+6567930788', {
        streetAddress: '#04-122 Tradehub 21, 18 Boon Lay Way',
        addressLocality: 'Singapore',
        postalCode: '609966',
        addressCountry: 'SG',
      }),
      orgPlace('Dexon Technology — Germany', '+4917617500750', {
        streetAddress: 'Am Hasenbiel 6',
        addressLocality: 'Stutensee',
        postalCode: '76297',
        addressCountry: 'DE',
      }),
      orgPlace('Dexon Technology USA — Houston', '+12818365424', {
        streetAddress: '11941 Cutten Rd, Suite 100',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        postalCode: '77066',
        addressCountry: 'US',
      }),
    ],

    'contactPoint': [
      orgContact('customer service', '+6633012484', 'info@dexon-technology.com'),
      orgContact('sales', '+66954742606', 'info@dexon-technology.com'),
      orgContact('human resources', '+66843629696'),
    ],

    'sameAs': [
      'https://www.linkedin.com/company/dexon-technology/',
      'https://www.youtube.com/c/DexonTechnology',
      'https://www.facebook.com/Dexontechnology/',
      'https://investor.dexon-technology.com/',
    ],

    /* Netherlands and Argentina removed per Bee, 17 Sep 2026, but both are still listed on the
       live site (Dacon Europe BV opened in the Netherlands in 2021). Site and schema disagree
       until one of them is updated. */
    'areaServed': ['Thailand', 'Singapore', 'Germany', 'United States', 'Indonesia', 'Philippines'].map(
      name => ({ '@type': 'Country', name }),
    ),

    'knowsAbout': [
      'Non-destructive testing',
      'Advanced non-destructive testing',
      'Pipeline in-line inspection',
      'Intelligent pigging',
      'Asset integrity management',
      'Risk-based inspection',
      'Pipeline integrity management',
      'Phased array ultrasonic testing',
      'Ultrasonic crack detection and sizing',
      'Magnetic flux leakage inspection',
      'Eddy current array testing',
      'API 510 pressure vessel inspection',
      'API 570 piping inspection',
      'API 653 storage tank inspection',
      'Heat exchanger and boiler tube inspection',
      'Wind turbine inspection',
      'Solar farm inspection',
      'Rope access inspection',
      'Drone and robotic inspection',
    ],

    'hasCertification': [
      orgCertification('ISO 9001 Quality Management Systems', 'BSI'),
      orgCertification('ISO 14001 Environmental Management Systems', 'BSI'),
      orgCertification('ISO 45001 Occupational Health and Safety Management Systems', 'BSI'),
    ],

    'memberOf': [
      { '@type': 'Organization', 'name': 'IRATA International' },
      { '@type': 'Organization', 'name': 'Lifting Equipment Engineers Association (LEEA)' },
      { '@type': 'Organization', 'name': 'Dropped Objects Prevention Scheme (DROPS)' },
    ],
  }

  org.logo = { '@type': 'ImageObject', 'url': LOGO_URL, 'width': 512, 'height': 512 }

  const website = {
    '@type': 'WebSite',
    '@id': SITE_ID,
    'url': `${ORG_SITE}/`,
    'name': 'Dexon Technology',
    'description':
      'Non-destructive testing, asset integrity management and pipeline in-line inspection services.',
    'publisher': { '@id': ORG_ID },
    'inLanguage': ['en', 'th'],
  }

  return [org, website]
}

function orgPlace(name: string, telephone: string, address: Record<string, string>) {
  return { '@type': 'Place', name, telephone, 'address': { '@type': 'PostalAddress', ...address } }
}

function orgContact(contactType: string, telephone: string, email?: string) {
  return {
    '@type': 'ContactPoint',
    contactType,
    telephone,
    ...(email ? { email } : {}),
    'availableLanguage': ['en', 'th'],
  }
}

function orgCertification(name: string, issuer: string) {
  return {
    '@type': 'Certification',
    name,
    'issuedBy': { '@type': 'Organization', 'name': issuer },
  }
}
